import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useReducer, useRef } from "react";
import type { CustomerAddress } from "@/features/customer/hooks/use-customer-addresses";
import {
  type AddressResolutionReason,
  useResolveDeliveryAddress,
} from "@/features/delivery/hooks/use-resolve-delivery-address";
import { createPinDebounce } from "@/features/delivery/lib/pin-debounce";
import { type GeolocationStatus, useGeolocation } from "@/hooks/use-geolocation";
import { requestJson } from "@/lib/api-client";
import { reverseGeocode } from "@/lib/maps/reverse-geocode";
import {
  createInitialDeliveryCheckoutState,
  deliveryCheckoutReducer,
  deriveDeliveryCheckout,
  getDeliveryPayLaterUnavailableReason,
  isCanonicalDeliveryAddressField,
  selectAvailablePaymentMode,
  selectDeliveryCheckoutAddress,
  selectInitialDeliveryCheckoutAddress,
  submitDeliveryCheckout,
  type CreateDeliveryOrderInput,
  type CreateDeliveryOrderResponse,
  type DeliveryCheckoutAddress,
  type DeliveryCheckoutAddressSelection,
  type DeliveryCheckoutBlockingReason,
  type DeliveryCheckoutBrowserLocationReason,
  type DeliveryCheckoutCartSnapshot,
  type DeliveryCheckoutParticipant,
  type DeliveryCheckoutPaymentMode,
  type DeliveryCheckoutPricingSnapshot,
  type DeliveryCheckoutSubmitResult,
} from "./delivery-checkout-model";

const RESOLVE_TEXT_DEBOUNCE_MS = 600;
const RESOLVE_PIN_DEBOUNCE_MS = 450;
const RESOLVE_SAVED_DEBOUNCE_MS = 300;

type BrowserLocationFailureStatus = Extract<
  GeolocationStatus,
  "denied" | "unavailable" | "timeout" | "unsupported"
>;

function browserLocationFailureReason(
  status: BrowserLocationFailureStatus,
): DeliveryCheckoutBrowserLocationReason {
  switch (status) {
    case "denied":
      return "geolocation_denied";
    case "unavailable":
      return "geolocation_unavailable";
    case "timeout":
      return "geolocation_timeout";
    case "unsupported":
      return "geolocation_unsupported";
  }
}

function isBrowserLocationFailure(
  status: GeolocationStatus,
): status is BrowserLocationFailureStatus {
  return ["denied", "unavailable", "timeout", "unsupported"].includes(status);
}

export type UseDeliveryCheckoutOptions = {
  orgSlug: string;
  checkoutReady: boolean;
  addresses: CustomerAddress[];
  participant: DeliveryCheckoutParticipant;
  payNowAvailable: boolean;
  cashOnDeliveryAvailable: boolean;
  cart: DeliveryCheckoutCartSnapshot;
  pricing: DeliveryCheckoutPricingSnapshot;
  inlineAddressLabel: string;
};

type ResolutionView =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "verified" }
  | {
      status: "blocked";
      reason: AddressResolutionReason;
      candidates: Array<{ line1: string; lat: number; lng: number }>;
    };

function resolutionView(
  resolution: ReturnType<typeof useResolveDeliveryAddress>["data"] | null,
  pending: boolean,
): ResolutionView {
  if (pending) {
    return { status: "pending" };
  }
  if (!resolution) {
    return { status: "idle" };
  }
  if (resolution.status === "verified" && resolution.deliverable && resolution.token) {
    return { status: "verified" };
  }
  return {
    candidates: resolution.reason === "ambiguous" ? resolution.candidates : [],
    reason: resolution.reason,
    status: "blocked",
  };
}

function classifySubmissionBlock(args: {
  phase: "render" | "submit";
  participant: DeliveryCheckoutParticipant;
  pending: boolean;
  itemCount: number;
  minOrderMet: boolean;
  inlineAddressValid: boolean;
  inlineBlockedByMap: boolean;
  resolutionReady: boolean;
  resolutionReason: AddressResolutionReason | null;
  paymentAllowed: boolean;
}): DeliveryCheckoutBlockingReason | null {
  if (args.participant.kind === "restaurant_account") {
    return "restaurant_account";
  }
  if (args.phase === "render" && args.pending) {
    return "submission_pending";
  }
  if (args.itemCount === 0) {
    return "empty_cart";
  }
  if (!args.minOrderMet) {
    return "minimum_order_not_met";
  }
  if (!args.inlineAddressValid) {
    return args.inlineBlockedByMap ? "map_unavailable" : "address_resolution_required";
  }
  if (args.phase === "render" && !args.resolutionReady) {
    return args.resolutionReason
      ? { kind: "address_resolution", code: args.resolutionReason }
      : "address_resolution_required";
  }
  return args.paymentAllowed ? null : "payment_unavailable";
}

export function useDeliveryCheckout(options: UseDeliveryCheckoutOptions) {
  const [state, dispatch] = useReducer(
    deliveryCheckoutReducer,
    selectInitialDeliveryCheckoutAddress(options.addresses),
    createInitialDeliveryCheckoutState,
  );
  const { mutateAsync: resolveAddress } = useResolveDeliveryAddress(options.orgSlug);
  const geolocation = useGeolocation();
  const createOrderMutation = useMutation({
    mutationFn: (input: CreateDeliveryOrderInput) =>
      requestJson<CreateDeliveryOrderResponse>("/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fulfillmentType: "delivery", ...input }),
      }),
  });
  const activeResolutionRequestRef = useRef(0);
  const addressSelectionTouchedRef = useRef(false);
  const inlineResolutionTimerRef = useRef<number | null>(null);
  const savedResolutionTimerRef = useRef<number | null>(null);
  const submissionInFlightRef = useRef(false);
  const browserLocationIntentRef = useRef(false);

  const cancelAutomaticResolutionTimers = useCallback(() => {
    if (inlineResolutionTimerRef.current != null) {
      window.clearTimeout(inlineResolutionTimerRef.current);
      inlineResolutionTimerRef.current = null;
    }
    if (savedResolutionTimerRef.current != null) {
      window.clearTimeout(savedResolutionTimerRef.current);
      savedResolutionTimerRef.current = null;
    }
  }, []);

  const resolveInlineAddress = useCallback(
    async (address: DeliveryCheckoutAddress) => {
      const requestId = ++activeResolutionRequestRef.current;
      dispatch({ type: "resolution-started", requestId });
      try {
        if (address.lat == null || address.lng == null) {
          dispatch({ type: "resolution-failed", requestId });
          return null;
        }
        const resolution = await resolveAddress({
          input: {
            city: address.city.trim() || null,
            country: address.country.trim() || "UY",
            label: address.label.trim() || null,
            lat: address.lat,
            line1: address.line1.trim(),
            line2: address.line2.trim() || null,
            lng: address.lng,
            notes: address.notes.trim() || null,
          },
        });
        if (requestId !== activeResolutionRequestRef.current) {
          return null;
        }
        dispatch({ type: "resolution-completed", requestId, resolution });
        return resolution;
      } catch (error) {
        dispatch({ type: "resolution-failed", requestId });
        if (requestId === activeResolutionRequestRef.current) {
          dispatch({ type: "error-recorded", error });
        }
        return null;
      }
    },
    [resolveAddress],
  );

  const resolveSavedAddress = useCallback(
    async (customerAddressId: string) => {
      const requestId = ++activeResolutionRequestRef.current;
      dispatch({ type: "resolution-started", requestId });
      try {
        const resolution = await resolveAddress({ customerAddressId });
        if (requestId !== activeResolutionRequestRef.current) {
          return null;
        }
        dispatch({ type: "resolution-completed", requestId, resolution });
        return resolution;
      } catch (error) {
        dispatch({ type: "resolution-failed", requestId });
        if (requestId === activeResolutionRequestRef.current) {
          dispatch({ type: "error-recorded", error });
        }
        return null;
      }
    },
    [resolveAddress],
  );

  const runReverseGeocode = useCallback(async (lat: number, lng: number, signal: AbortSignal) => {
    dispatch({ type: "geocoding-started" });
    try {
      const result = await reverseGeocode(lat, lng, signal);
      if (!signal.aborted) {
        dispatch({
          type: "geocoding-completed",
          address: {
            city: result.city ?? "",
            country: result.country ?? "",
            line1: result.line1 ?? "",
            line2: result.line2 ?? "",
          },
        });
      }
    } catch {
      if (!signal.aborted) {
        dispatch({ type: "geocoding-failed" });
      }
    }
  }, []);
  const runReverseGeocodeRef = useRef(runReverseGeocode);
  runReverseGeocodeRef.current = runReverseGeocode;
  const pinDebounceRef = useRef<ReturnType<typeof createPinDebounce> | null>(null);
  if (!pinDebounceRef.current) {
    pinDebounceRef.current = createPinDebounce({
      debounceMs: RESOLVE_PIN_DEBOUNCE_MS,
      onSettle: (input) => runReverseGeocodeRef.current(input.lat, input.lng, input.signal),
    });
  }

  useEffect(() => {
    return () => {
      pinDebounceRef.current?.cancel();
      activeResolutionRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (addressSelectionTouchedRef.current || state.addressSelection.kind !== "inline") {
      return;
    }
    const initialSelection = selectInitialDeliveryCheckoutAddress(options.addresses);
    if (initialSelection.kind === "saved") {
      dispatch({ type: "address-selection-changed", selection: initialSelection });
    }
  }, [options.addresses, state.addressSelection.kind]);

  const addressSelection = selectDeliveryCheckoutAddress(state.addressSelection, options.addresses);
  const savedAddressId =
    addressSelection.kind === "saved" ? addressSelection.customerAddressId : null;
  const selectedAddress =
    addressSelection.kind === "saved"
      ? options.addresses.find((address) => address.id === addressSelection.customerAddressId)
      : undefined;
  const usesInlineAddress = addressSelection.kind === "inline";
  const payLaterUnavailableReason = getDeliveryPayLaterUnavailableReason({
    cashOnDeliveryAvailable: options.cashOnDeliveryAvailable,
    participant: options.participant,
  });
  const canUsePayLater = payLaterUnavailableReason == null;
  const derived = deriveDeliveryCheckout(state, {
    canUsePayLater,
    deliveryMinimumOrder: options.pricing.deliveryMinimumOrder,
    itemCount: options.cart.itemCount,
    payNowAvailable: options.payNowAvailable,
    savedAddressId,
    subtotal: options.pricing.subtotal,
    usesInlineAddress,
  });

  useEffect(() => {
    const paymentMode = selectAvailablePaymentMode(
      state.paymentMode,
      options.payNowAvailable,
      canUsePayLater,
    );
    if (paymentMode !== state.paymentMode) {
      dispatch({ type: "payment-mode-changed", paymentMode });
    }
  }, [canUsePayLater, options.payNowAvailable, state.paymentMode]);

  useEffect(() => {
    if (
      !options.checkoutReady ||
      !usesInlineAddress ||
      state.deliveryAddress.lat == null ||
      state.deliveryAddress.lng == null ||
      state.deliveryAddress.line1.trim().length === 0
    ) {
      return;
    }
    const handle = window.setTimeout(() => {
      inlineResolutionTimerRef.current = null;
      void resolveInlineAddress(state.deliveryAddress);
    }, RESOLVE_TEXT_DEBOUNCE_MS);
    inlineResolutionTimerRef.current = handle;
    return () => {
      window.clearTimeout(handle);
      if (inlineResolutionTimerRef.current === handle) {
        inlineResolutionTimerRef.current = null;
      }
    };
  }, [
    options.checkoutReady,
    resolveInlineAddress,
    state.deliveryAddress.city,
    state.deliveryAddress.country,
    state.deliveryAddress.lat,
    state.deliveryAddress.line1,
    state.deliveryAddress.lng,
    usesInlineAddress,
  ]);

  useEffect(() => {
    if (!options.checkoutReady || !savedAddressId) {
      return;
    }
    const handle = window.setTimeout(() => {
      savedResolutionTimerRef.current = null;
      void resolveSavedAddress(savedAddressId);
    }, RESOLVE_SAVED_DEBOUNCE_MS);
    savedResolutionTimerRef.current = handle;
    return () => {
      window.clearTimeout(handle);
      if (savedResolutionTimerRef.current === handle) {
        savedResolutionTimerRef.current = null;
      }
    };
  }, [options.checkoutReady, resolveSavedAddress, savedAddressId]);

  const selectAddress = useCallback((selection: DeliveryCheckoutAddressSelection) => {
    addressSelectionTouchedRef.current = true;
    activeResolutionRequestRef.current += 1;
    dispatch({ type: "address-selection-changed", selection });
  }, []);
  const updateInlineAddress = useCallback(
    <Field extends keyof DeliveryCheckoutAddress>(
      field: Field,
      value: DeliveryCheckoutAddress[Field],
    ) => {
      if (isCanonicalDeliveryAddressField(field)) {
        activeResolutionRequestRef.current += 1;
      }
      dispatch({ type: "address-field-changed", field, value });
    },
    [],
  );
  const moveMapPin = useCallback(
    (next: { lat: number; lng: number }) => {
      activeResolutionRequestRef.current += 1;
      dispatch({ type: "map-pin-changed", lat: next.lat, lng: next.lng });
      if (options.participant.kind !== "guest") {
        pinDebounceRef.current?.move(next);
      }
    },
    [options.participant.kind],
  );
  useEffect(() => {
    if (browserLocationIntentRef.current && isBrowserLocationFailure(geolocation.status)) {
      browserLocationIntentRef.current = false;
      dispatch({
        type: "browser-location-reason-changed",
        reason: browserLocationFailureReason(geolocation.status),
      });
      return;
    }
    if (!options.checkoutReady || geolocation.status !== "granted" || !geolocation.coords) {
      return;
    }
    browserLocationIntentRef.current = false;
    moveMapPin(geolocation.coords);
    geolocation.reset();
  }, [
    geolocation.coords,
    geolocation.reset,
    geolocation.status,
    moveMapPin,
    options.checkoutReady,
  ]);
  const useBrowserLocation = useCallback(() => {
    dispatch({ type: "browser-location-reason-changed", reason: null });
    if (geolocation.status === "granted" && geolocation.coords) {
      browserLocationIntentRef.current = false;
      moveMapPin(geolocation.coords);
      geolocation.reset();
      return;
    }
    browserLocationIntentRef.current = true;
    if (geolocation.status === "unsupported") {
      browserLocationIntentRef.current = false;
      dispatch({
        type: "browser-location-reason-changed",
        reason: browserLocationFailureReason(geolocation.status),
      });
      return;
    }
    geolocation.request();
  }, [geolocation.coords, geolocation.request, geolocation.reset, geolocation.status, moveMapPin]);
  const reportMapLocationFailure = useCallback((status: BrowserLocationFailureStatus) => {
    dispatch({
      type: "browser-location-reason-changed",
      reason: browserLocationFailureReason(status),
    });
  }, []);
  const reportMapUnavailable = useCallback(() => {
    activeResolutionRequestRef.current += 1;
    dispatch({ type: "map-load-failed" });
  }, []);
  const applyAddressCandidate = useCallback(
    (candidate: { line1: string; lat: number; lng: number }) => {
      activeResolutionRequestRef.current += 1;
      const nextAddress = {
        ...state.deliveryAddress,
        lat: candidate.lat,
        line1: candidate.line1,
        lng: candidate.lng,
      };
      dispatch({ type: "address-replaced", address: nextAddress });
      void resolveInlineAddress(nextAddress);
    },
    [resolveInlineAddress, state.deliveryAddress],
  );
  const setContactName = useCallback(
    (value: string) => dispatch({ type: "text-field-changed", field: "contactName", value }),
    [],
  );
  const setContactPhone = useCallback(
    (value: string) => dispatch({ type: "text-field-changed", field: "contactPhone", value }),
    [],
  );
  const setCustomerAddressLabel = useCallback(
    (value: string) =>
      dispatch({ type: "text-field-changed", field: "customerAddressLabel", value }),
    [],
  );
  const setNotes = useCallback(
    (value: string) => dispatch({ type: "text-field-changed", field: "notes", value }),
    [],
  );
  const selectPayment = useCallback(
    (paymentMode: DeliveryCheckoutPaymentMode) => {
      const available = paymentMode === "pay_now" ? options.payNowAvailable : canUsePayLater;
      if (available) {
        dispatch({ type: "payment-mode-changed", paymentMode });
      }
    },
    [canUsePayLater, options.payNowAvailable],
  );
  const setSaveInlineAddress = useCallback(
    (value: boolean) => dispatch({ type: "save-address-changed", value }),
    [],
  );
  const dismissError = useCallback(() => dispatch({ type: "error-cleared" }), []);

  const pending = state.resolutionPending || createOrderMutation.isPending;
  const currentResolutionView = resolutionView(state.resolution, state.resolutionPending);
  const blockContext = {
    inlineAddressValid: derived.inlineAddressValid,
    inlineBlockedByMap: derived.inlineBlockedByMap,
    itemCount: options.cart.itemCount,
    minOrderMet: derived.minOrderMet,
    participant: options.participant,
    paymentAllowed: derived.paymentAllowed,
    pending,
    resolutionReady: derived.resolutionReady,
    resolutionReason:
      currentResolutionView.status === "blocked" ? currentResolutionView.reason : null,
  };
  const blockedReason = classifySubmissionBlock({ ...blockContext, phase: "render" });
  const submitBlockingReason = classifySubmissionBlock({ ...blockContext, phase: "submit" });

  const submit = useCallback(async (): Promise<DeliveryCheckoutSubmitResult> => {
    if (submitBlockingReason) {
      return { status: "blocked", reason: submitBlockingReason };
    }
    if (submissionInFlightRef.current) {
      return { status: "blocked", reason: "submission_pending" };
    }
    submissionInFlightRef.current = true;
    try {
      cancelAutomaticResolutionTimers();
      return await submitDeliveryCheckout({
        adapters: {
          createOrder: createOrderMutation.mutateAsync,
          resolveInlineAddress,
          resolveSavedAddress,
        },
        cartLines: options.cart.lines,
        inlineAddressLabel: options.inlineAddressLabel,
        isAuthenticatedConsumer: options.participant.kind === "consumer",
        orgSlug: options.orgSlug,
        savedAddressId,
        state,
      });
    } finally {
      submissionInFlightRef.current = false;
    }
  }, [
    cancelAutomaticResolutionTimers,
    createOrderMutation.mutateAsync,
    options.cart.lines,
    options.inlineAddressLabel,
    options.orgSlug,
    options.participant.kind,
    resolveInlineAddress,
    resolveSavedAddress,
    savedAddressId,
    state,
    submitBlockingReason,
  ]);

  const addressChoices = options.addresses.map((address) => ({
    city: address.city,
    customerAddressId: address.id,
    label: address.label,
    line1: address.line1,
    line2: address.line2,
  }));
  const activeAddress =
    usesInlineAddress || !selectedAddress
      ? {
          kind: "inline" as const,
          value: state.deliveryAddress,
          map: {
            browserLocation: {
              feedbackReason: state.browserLocationReason,
              pending: geolocation.status === "requesting",
            },
            isGeocoding: state.isGeocoding,
            unavailable: state.mapUnavailable,
          },
          resolution: currentResolutionView,
          save: {
            available: options.participant.kind === "consumer",
            enabled: state.saveAsCustomerAddress,
            inputEnabled: currentResolutionView.status === "verified",
            label: state.customerAddressLabel,
          },
        }
      : {
          kind: "saved" as const,
          customerAddressId: selectedAddress.id,
          resolution: currentResolutionView,
        };

  return {
    access:
      options.participant.kind === "restaurant_account"
        ? ({ kind: "restaurant_account" } as const)
        : ({ kind: "allowed" } as const),
    address: {
      active: activeAddress,
      choices: addressChoices,
      contact: { name: state.contactName, phone: state.contactPhone },
    },
    payment: {
      selected: state.paymentMode,
      payNow: {
        available: options.payNowAvailable,
        unavailableReason: options.payNowAvailable ? null : ("not_offered" as const),
      },
      payLater: {
        available: canUsePayLater,
        unavailableReason: payLaterUnavailableReason,
      },
    },
    orderSummary: {
      deliveryFee: options.pricing.deliveryFee,
      minimumOrder: {
        amount: options.pricing.deliveryMinimumOrder,
        met: derived.minOrderMet,
      },
      subtotal: options.pricing.subtotal,
      total: options.pricing.subtotal + options.pricing.deliveryFee,
    },
    notes: state.notes,
    submission: {
      blockedReason,
      enabled: blockedReason == null,
      pending,
      submit,
    },
    feedback: { error: state.error, dismissError },
    actions: {
      applyAddressCandidate,
      moveMapPin,
      reportMapUnavailable,
      reportMapLocationFailure,
      selectAddress,
      selectPayment,
      setContactName,
      setContactPhone,
      setNotes,
      setSaveInlineAddress,
      setInlineAddressSaveLabel: setCustomerAddressLabel,
      updateInlineAddress,
      useBrowserLocation,
    },
  };
}
